import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import {
  Users,
  UserPlus,
  Trophy,
  TrendingUp,
  Clock,
  Calendar,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Link,
  Activity,
  Award,
  Hash,
  Mail,
  CheckCircle,
  XCircle,
  Copy,
  Check,
  Filter,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';

const PRESETS = [
  { key: '7d', label: '7 Days' },
  { key: '30d', label: '30 Days' },
  { key: '90d', label: '90 Days' },
  { key: 'thisMonth', label: 'This Month' },
  { key: 'lastMonth', label: 'Last Month' },
  { key: 'thisQuarter', label: 'This Quarter' },
  { key: 'thisYear', label: 'This Year' },
  { key: 'allTime', label: 'All Time' },
];

export default function StaffReferrals() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedStaff, setExpandedStaff] = useState({});
  const [copiedCode, setCopiedCode] = useState(null);

  // Date filter state — default to "This Month"
  const [activePreset, setActivePreset] = useState('thisMonth');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [appliedFilter, setAppliedFilter] = useState({ preset: 'thisMonth' });

  const fetchData = useCallback(async (filter) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filter?.preset) params.append('preset', filter.preset);
      if (filter?.startDate) params.append('startDate', filter.startDate);
      if (filter?.endDate) params.append('endDate', filter.endDate);
      const qs = params.toString();
      const response = await api.get(`/admin/staff-referrals${qs ? '?' + qs : ''}`);
      setData(response.data);
    } catch (err) {
      toast.error('Failed to load referral data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(appliedFilter); }, []);

  const applyPreset = (presetKey) => {
    if (activePreset === presetKey) {
      // Toggle off
      setActivePreset(null);
      setCustomStart('');
      setCustomEnd('');
      setAppliedFilter(null);
      fetchData(null);
      return;
    }
    setActivePreset(presetKey);
    setCustomStart('');
    setCustomEnd('');
    const filter = { preset: presetKey };
    setAppliedFilter(filter);
    fetchData(filter);
  };

  const applyCustomRange = () => {
    if (!customStart && !customEnd) return;
    setActivePreset(null);
    const filter = {};
    if (customStart) filter.startDate = customStart;
    if (customEnd) filter.endDate = customEnd;
    setAppliedFilter(filter);
    fetchData(filter);
  };

  const clearFilter = () => {
    setActivePreset(null);
    setCustomStart('');
    setCustomEnd('');
    setAppliedFilter(null);
    fetchData(null);
  };

  const getFilterLabel = () => {
    if (!appliedFilter) return null;
    if (appliedFilter.preset) {
      return PRESETS.find(p => p.key === appliedFilter.preset)?.label || appliedFilter.preset;
    }
    const parts = [];
    if (appliedFilter.startDate) parts.push(new Date(appliedFilter.startDate).toLocaleDateString());
    if (appliedFilter.endDate) parts.push(new Date(appliedFilter.endDate).toLocaleDateString());
    return parts.join(' – ');
  };

  const toggleStaff = (id) => {
    setExpandedStaff(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Referral code copied!');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center text-gray-400 py-12">
        <p>Failed to load referral data.</p>
        <button onClick={() => fetchData(appliedFilter)} className="mt-4 px-4 py-2 bg-amber-500 text-black rounded-lg hover:bg-amber-400">
          Retry
        </button>
      </div>
    );
  }

  const { summary, leaderboard } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <UserPlus className="text-amber-500" size={28} />
            Staff Referral Tracker
          </h1>
          <p className="text-gray-400 mt-1">Track referrals made by staff and admin team members</p>
        </div>
        <button
          onClick={() => fetchData(appliedFilter)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <Users size={16} />
            Total Referrals
          </div>
          <p className="text-2xl font-bold text-white">{summary.totalReferrals}</p>
        </div>
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <Calendar size={16} />
            Last 30 Days
          </div>
          <p className="text-2xl font-bold text-amber-400">{summary.referralsLast30Days}</p>
        </div>
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <Clock size={16} />
            Last 7 Days
          </div>
          <p className="text-2xl font-bold text-green-400">{summary.referralsLast7Days}</p>
        </div>
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <Link size={16} />
            PC Linked
          </div>
          <p className="text-2xl font-bold text-blue-400">
            {summary.referralsWithPlayerClub}
            <span className="text-sm font-normal text-gray-400 ml-1">({summary.conversionRate}%)</span>
          </p>
        </div>
      </div>

      {/* Date Filter */}
      <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-gray-400 flex items-center gap-2">
            <Filter size={16} />
            Filter by Date Range
          </h3>
          {appliedFilter && (
            <button
              onClick={clearFilter}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-white px-2 py-1 bg-gray-700 rounded-lg transition-colors"
            >
              <X size={12} />
              Clear Filter
            </button>
          )}
        </div>

        {/* Quick Presets */}
        <div className="flex flex-wrap gap-2 mb-3">
          {PRESETS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => applyPreset(key)}
              className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                activePreset === key
                  ? 'bg-amber-500 text-black'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Custom Date Range */}
        <div className="flex items-center gap-3">
          <input
            type="date"
            value={customStart}
            onChange={(e) => setCustomStart(e.target.value)}
            className="bg-gray-700 border border-gray-600 text-white text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-500"
            placeholder="Start date"
          />
          <span className="text-gray-500 text-sm">to</span>
          <input
            type="date"
            value={customEnd}
            onChange={(e) => setCustomEnd(e.target.value)}
            className="bg-gray-700 border border-gray-600 text-white text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-500"
            placeholder="End date"
          />
          <button
            onClick={applyCustomRange}
            disabled={!customStart && !customEnd}
            className="px-4 py-1.5 text-sm bg-amber-500 text-black rounded-lg font-medium hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Apply
          </button>
        </div>

        {/* Active Filter Badge */}
        {appliedFilter && data?.summary?.filteredReferralCount != null && (
          <div className="mt-3 flex items-center gap-2 text-sm">
            <span className="text-gray-400">Showing:</span>
            <span className="bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-lg font-medium">
              {data.summary.filteredReferralCount} referral{data.summary.filteredReferralCount !== 1 ? 's' : ''}
            </span>
            <span className="text-gray-500">in {getFilterLabel()}</span>
          </div>
        )}
      </div>

      {/* Activity Generated */}
      <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
        <h3 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
          <Activity size={16} />
          Activity Generated by Referrals
          {appliedFilter && summary.filteredActivityGenerated && (
            <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-lg ml-2">{getFilterLabel()}</span>
          )}
        </h3>
        <div className="grid grid-cols-3 gap-4">
          {(() => {
            const activity = appliedFilter && summary.filteredActivityGenerated ? summary.filteredActivityGenerated : summary.activityGenerated;
            return (
              <>
                <div>
                  <p className="text-lg font-bold text-white">{activity.totalSessions}</p>
                  <p className="text-xs text-gray-400">Total Sessions</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-white">{activity.totalHours}h</p>
                  <p className="text-xs text-gray-400">Hours Played</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-white">{activity.totalPoints.toLocaleString()}</p>
                  <p className="text-xs text-gray-400">Points Earned</p>
                </div>
              </>
            );
          })()}
        </div>
      </div>

      {/* Leaderboard */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Trophy className="text-amber-500" size={20} />
            Staff Leaderboard
          </h2>
        </div>

        {leaderboard.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            No staff referrals found yet.
          </div>
        ) : (
          <div className="divide-y divide-gray-700">
            {leaderboard.map((staff, index) => (
              <div key={staff.id}>
                {/* Staff Row */}
                <div
                  className="px-5 py-3 flex items-center gap-4 hover:bg-gray-700/30 cursor-pointer transition-colors"
                  onClick={() => staff.totalReferrals > 0 && toggleStaff(staff.id)}
                >
                  {/* Rank */}
                  <div className="w-8 text-center">
                    {index === 0 && staff.totalReferrals > 0 ? (
                      <span className="text-xl">🥇</span>
                    ) : index === 1 && staff.totalReferrals > 0 ? (
                      <span className="text-xl">🥈</span>
                    ) : index === 2 && staff.totalReferrals > 0 ? (
                      <span className="text-xl">🥉</span>
                    ) : (
                      <span className="text-gray-500 text-sm font-medium">#{index + 1}</span>
                    )}
                  </div>

                  {/* Staff Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-white truncate">{staff.name}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        staff.role === 'admin' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        {staff.role}
                      </span>
                    </div>
                    {staff.referralCode && (
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-xs text-gray-500 font-mono">{staff.referralCode}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); copyCode(staff.referralCode); }}
                          className="text-gray-500 hover:text-gray-300 transition-colors"
                        >
                          {copiedCode === staff.referralCode ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-6 text-sm">
                    {appliedFilter ? (
                      <div className="text-center">
                        <p className="font-bold text-amber-400">{staff.filteredReferrals ?? 0}</p>
                        <p className="text-xs text-gray-500">{getFilterLabel()}</p>
                      </div>
                    ) : (
                      <>
                        <div className="text-center">
                          <p className="font-bold text-white">{staff.totalReferrals}</p>
                          <p className="text-xs text-gray-500">Total</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-amber-400">{staff.referralsLast30Days}</p>
                          <p className="text-xs text-gray-500">30d</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-green-400">{staff.referralsLast7Days}</p>
                          <p className="text-xs text-gray-500">7d</p>
                        </div>
                      </>
                    )}
                    {staff.totalReferrals > 0 && (
                      expandedStaff[staff.id]
                        ? <ChevronDown size={16} className="text-gray-400" />
                        : <ChevronRight size={16} className="text-gray-400" />
                    )}
                  </div>
                </div>

                {/* Expanded Referral Details */}
                {expandedStaff[staff.id] && staff.referrals.length > 0 && (
                  <div className="bg-gray-900/50 px-5 py-3">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-gray-500 text-xs uppercase">
                          <th className="text-left py-2">Referred User</th>
                          <th className="text-left py-2">Signed Up</th>
                          <th className="text-center py-2">PC Linked</th>
                          <th className="text-center py-2">Sessions</th>
                          <th className="text-center py-2">Hours</th>
                          <th className="text-center py-2">Points</th>
                          <th className="text-center py-2">Last Login</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-800">
                        {staff.referrals.map((ref) => (
                          <tr key={ref.id} className="hover:bg-gray-800/50">
                            <td className="py-2">
                              <div className="flex items-center gap-2">
                                <span className="text-white">{ref.name}</span>
                                {!ref.isActive && (
                                  <span className="text-xs bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded">Inactive</span>
                                )}
                              </div>
                              <span className="text-xs text-gray-500">{ref.email}</span>
                            </td>
                            <td className="py-2 text-gray-400">{formatDateShort(ref.createdAt)}</td>
                            <td className="py-2 text-center">
                              {ref.hasPlayerClub ? (
                                <CheckCircle size={16} className="text-green-400 inline" />
                              ) : (
                                <XCircle size={16} className="text-gray-600 inline" />
                              )}
                            </td>
                            <td className="py-2 text-center text-gray-300">{ref.activity.sessionCount}</td>
                            <td className="py-2 text-center text-gray-300">{ref.activity.totalHours}</td>
                            <td className="py-2 text-center text-gray-300">{ref.activity.totalPoints.toLocaleString()}</td>
                            <td className="py-2 text-center text-gray-400 text-xs">{formatDateShort(ref.lastLoginAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
